/**
 * SCRUM-208 — Tenant inbox: DSAR (Data Subject Access Requests).
 * Listado y proceso de EXPORT/DELETE de datos personales.
 * Gate: can_manage_orders (mismo patrón que /cumplimiento/solicitudes SCRUM-207).
 *
 * No usa APIs nativas de confirm/alert/prompt — todo pasa por AppDialogs.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Shield,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Download,
  Trash2,
} from "lucide-react";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import type {
  PrivacyRequest,
  PrivacyRequestStatus,
  PrivacyRequestType,
} from "@/lib/db-service/privacy-requests";

export const Route = createFileRoute("/app/cumplimiento/privacidad")({
  component: PrivacidadPage,
  head: () => ({ meta: [{ title: "Privacidad · DSAR (Ley 25.326)" }] }),
});

type ListResult = { items: PrivacyRequest[]; total: number };

const STATUS_LABELS: Record<PrivacyRequestStatus, string> = {
  PENDING: "Pendiente",
  PROCESSING: "En proceso",
  COMPLETED: "Completada",
  REJECTED: "Rechazada",
};

const STATUS_COLORS: Record<PrivacyRequestStatus, string> = {
  PENDING: "bg-yellow-100 text-yellow-800",
  PROCESSING: "bg-blue-100 text-blue-800",
  COMPLETED: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-800",
};

const TYPE_LABELS: Record<PrivacyRequestType, string> = {
  EXPORT: "Exportar datos",
  DELETE: "Eliminar datos",
};

const PAGE_SIZE = 20;

function PrivacidadPage() {
  const tid = useWorkspaceTenantId();
  const [items, setItems] = useState<PrivacyRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter !== "all") params.set("type", typeFilter);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      const data = await apiGetJson<ListResult>(`/api/privacy-requests?${params}`, tid);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error("Error al cargar solicitudes de privacidad");
    } finally {
      setLoading(false);
    }
  }, [tid, page, statusFilter, typeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading && items.length === 0) return <Loading />;

  return (
    <div className="space-y-4 p-4 md:p-6">
      <CrudShellHeader
        title="Privacidad · DSAR"
        subtitle="Solicitudes de exportación y eliminación de datos personales (Ley 25.326 / GDPR)."
      />
      <MerchantExperienceBar screenId="cumplimiento-privacidad" />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="PENDING">Pendiente</SelectItem>
            <SelectItem value="PROCESSING">En proceso</SelectItem>
            <SelectItem value="COMPLETED">Completada</SelectItem>
            <SelectItem value="REJECTED">Rechazada</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="EXPORT">Exportar datos</SelectItem>
            <SelectItem value="DELETE">Eliminar datos</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>

        <span className="ml-auto text-sm text-muted-foreground">
          {total} solicitud{total !== 1 ? "es" : ""}
        </span>
      </div>

      {items.length === 0 && !loading ? (
        <CrudEmptyState
          icon={Shield}
          title="Sin solicitudes de privacidad"
          description="Las solicitudes de exportación o eliminación de datos aparecerán acá."
        />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card
              key={item.id}
              className={`cursor-pointer transition-shadow hover:shadow-md ${selectedId === item.id ? "ring-2 ring-primary" : ""}`}
              onClick={() => setSelectedId(selectedId === item.id ? null : item.id)}
            >
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold tracking-wider">
                    {item.referenceCode}
                  </span>
                  <Badge className={STATUS_COLORS[item.status]}>
                    {STATUS_LABELS[item.status]}
                  </Badge>
                  <Badge variant="outline" className="gap-1">
                    {item.type === "EXPORT" ? (
                      <Download className="h-3 w-3" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                    {TYPE_LABELS[item.type]}
                  </Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(item.createdAt).toLocaleDateString("es-AR", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-0 text-sm">
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground">
                  <span>
                    <strong>Email:</strong> {item.requesterEmail}
                  </span>
                  {item.requesterName ? (
                    <span>
                      <strong>Nombre:</strong> {item.requesterName}
                    </span>
                  ) : null}
                  {item.requesterDoc ? (
                    <span>
                      <strong>Doc:</strong> {item.requesterDoc}
                    </span>
                  ) : null}
                  {item.customerId ? (
                    <span className="text-xs">
                      <Badge variant="outline">Cliente autenticado</Badge>
                    </span>
                  ) : (
                    <span className="text-xs">
                      <Badge variant="outline">Guest</Badge>
                    </span>
                  )}
                </div>
                {item.notes ? (
                  <p className="mt-2 whitespace-pre-line text-xs text-muted-foreground">
                    {item.notes}
                  </p>
                ) : null}

                {selectedId === item.id ? (
                  <PrivacyActions item={item} tid={tid!} onUpdated={load} />
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 ? (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function PrivacyActions({
  item,
  tid,
  onUpdated,
}: {
  item: PrivacyRequest;
  tid: string;
  onUpdated: () => void;
}) {
  const { confirm, alert } = useAppDialogs();
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(item.notes ?? "");

  const terminal = item.status === "COMPLETED" || item.status === "REJECTED";

  const patch = async (
    status: PrivacyRequestStatus,
    action?: "export" | "delete",
    confirmMsg?: { title: string; description: string; destructive?: boolean },
  ) => {
    if (confirmMsg) {
      const ok = await confirm(confirmMsg);
      if (!ok) return;
    }
    setBusy(true);
    try {
      await apiSendJson(
        "/api/privacy-requests",
        "PATCH",
        { id: item.id, status, notes: notes || undefined, action },
        tid,
      );
      toast.success("Solicitud actualizada");
      onUpdated();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error al actualizar";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const downloadExport = async () => {
    if (!item.exportPayload) return;
    const blob = new Blob([JSON.stringify(item.exportPayload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${item.referenceCode}-datos.json`;
    a.click();
    URL.revokeObjectURL(url);
    await alert({
      title: "Export descargado",
      description: `El archivo ${item.referenceCode}-datos.json contiene los datos personales del cliente.`,
    });
  };

  return (
    <div className="mt-4 space-y-3 rounded-lg border bg-muted/30 p-3">
      <div>
        <label className="mb-1 block text-xs font-medium">Notas / motivo</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
          rows={2}
          placeholder="Notas internas, motivo de rechazo, ticket de referencia…"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {!terminal && item.status === "PENDING" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void patch("PROCESSING")}
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Marcar en proceso
          </Button>
        ) : null}

        {!terminal && item.type === "EXPORT" ? (
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              void patch("COMPLETED", "export", {
                title: "Generar export y marcar completada",
                description:
                  "Se generará un snapshot JSON con todos los datos personales del cliente (perfil, direcciones, órdenes). El archivo expira a los 30 días.",
                confirmLabel: "Generar export",
              })
            }
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            Generar export & completar
          </Button>
        ) : null}

        {!terminal && item.type === "DELETE" ? (
          <Button
            size="sm"
            variant="destructive"
            disabled={busy}
            onClick={() =>
              void patch("COMPLETED", "delete", {
                title: "Anonimizar datos del cliente",
                description:
                  "Se reemplazarán nombre/email/teléfono por placeholders y se borrarán las direcciones. Las órdenes se conservan por obligación fiscal (AFIP). Esta acción no se puede deshacer.",
                confirmLabel: "Sí, anonimizar",
                destructive: true,
              })
            }
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
            Anonimizar & completar
          </Button>
        ) : null}

        {!terminal ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              void patch("REJECTED", undefined, {
                title: "Rechazar solicitud",
                description:
                  "La solicitud quedará marcada como rechazada. Recordá dejar el motivo en las notas antes de confirmar.",
                destructive: true,
                confirmLabel: "Rechazar",
              })
            }
          >
            Rechazar
          </Button>
        ) : null}

        {terminal && item.type === "EXPORT" && item.exportPayload ? (
          <Button size="sm" variant="outline" onClick={downloadExport}>
            <Download className="mr-1.5 h-4 w-4" />
            Descargar JSON
          </Button>
        ) : null}
      </div>

      {item.exportExpiresAt ? (
        <p className="text-xs text-muted-foreground">
          Export expira: {new Date(item.exportExpiresAt).toLocaleString("es-AR")}
        </p>
      ) : null}
    </div>
  );
}
